import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowForwardCallStep } from 'entities/SmartCall/api/types'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { NumberSettingStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/ForwardCall/ui/ForwardCallActionEditContent/NumberSetting/store/NumberSettingStore'
import {
  allSmartCallFlowActionTypes,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type IForwardCallStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowForwardCallStep['args']
}

export class ForwardCallStore {
  static readonly type = 'forwardCall' satisfies ISmartCallFlowActionType
  static title = 'Forward call'
  static iconProps: IIconProps = {
    icon: 'phoneTransfer',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: IForwardCallStoreProps,
    private parent: { inbox: Inbox | null }
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: ForwardCallStore.type,
      name: ForwardCallStore.title,
      allowedBeforeActions: allSmartCallFlowActionTypes.filter((type) => type !== 'forwardCall'),
      allowedNextActions: [],
    })

    this._numbersSettingsMap = new Map(
      this.parent.inbox?.numbers.map((number) => {
        const config = args?.find((item) => item.number_id === number.id)
        return [number.id, new NumberSettingStore(this._entities, number, config)]
      })
    )

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowForwardCallStep, 'id' | 'type'> = {
    id: 'forwardCallMulti',
    type: 'action',
  }

  get step(): ISmartCallFlowForwardCallStep {
    return {
      ...this._stepIdType,
      args: this.numbersSettings.map((number) => number.params),
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {}

  private _isShowConfigurationError = false
  validate = () => {
    if (this._numbersSettingsMap.size === 1) {
      if (!this.numbersSettings[0].validate()) {
        return false
      }
    } else {
      if (!this.numbersSettings.some((item) => item.isConfigured)) {
        this._isShowConfigurationError = true
        return false
      }
    }

    return true
  }

  get isConfigurationError() {
    if (this._isShowConfigurationError && !this.numbersSettings.some((item) => item.isConfigured)) {
      return true
    }
    return false
  }

  private _numbersSettingsMap: Map<number, NumberSettingStore>
  replaceNumberSettings = (id: number, store: NumberSettingStore) => {
    this._numbersSettingsMap.set(id, store)
    this._isShowConfigurationError = false
  }

  get numbersSettings() {
    return Array.from(this._numbersSettingsMap.values())
  }

  get configuredNumbers() {
    return this.numbersSettings.filter((item) => item.isConfigured)
  }
  get notConfiguredNumbers() {
    return this.numbersSettings.filter((item) => !item.isConfigured)
  }
}
